// Intercambio share-compliance gate — client wrapper around the
// check-share-compliance edge function (phone-in-description regex +
// Gemini watermark check on the cover photo).
//
// Fail-open by design: if the edge function or the AI provider is down we
// allow the share rather than blocking agents. Keep in sync with
// mobile/utils/share-compliance.ts.

import type { SupabaseClient } from '@supabase/supabase-js';

export interface ShareComplianceResult {
  allowed: boolean;
  reasons: string[];
}

export async function checkShareCompliance(
  supabase: SupabaseClient,
  propertyId: string,
): Promise<ShareComplianceResult> {
  try {
    const { data, error } = await supabase.functions.invoke('check-share-compliance', {
      body: { property_id: propertyId },
    });
    if (error || !data) return { allowed: true, reasons: [] };
    return {
      allowed: data.allowed !== false,
      reasons: Array.isArray(data.reasons) ? data.reasons : [],
    };
  } catch {
    return { allowed: true, reasons: [] };
  }
}

/**
 * Post-save enforcement for flows that write share_status='shared' directly
 * (property form saves). If the property violates the Intercambio rules,
 * flips it back to not_shared and returns the Spanish reason for the caller
 * to alert; returns null when compliant.
 */
/**
 * Files an appeal against a share-compliance block/un-share. Returns a
 * Spanish message for the UI regardless of outcome. Keep in sync with
 * mobile/utils/share-compliance.ts.
 */
export async function submitShareAppeal(
  supabase: SupabaseClient,
  propertyId: string,
  message?: string,
): Promise<{ ok: boolean; message: string }> {
  const { data, error } = await supabase.rpc('appeal_share_compliance', {
    p_property_id: propertyId,
    p_message: message ?? null,
  });
  if (error) return { ok: false, message: `No se pudo enviar la apelación: ${error.message}` };
  if (data?.error) return { ok: false, message: data.message || 'No se pudo enviar la apelación.' };
  return { ok: true, message: 'Apelación enviada. Tu propiedad seguirá compartida en Intercambio mientras un administrador la revisa.' };
}

export async function enforceShareComplianceAfterSave(
  supabase: SupabaseClient,
  propertyId: string,
): Promise<string | null> {
  const res = await checkShareCompliance(supabase, propertyId);
  if (res.allowed) return null;
  await supabase
    .from('properties')
    .update({ share_status: 'not_shared', unshared_at: new Date().toISOString() })
    .eq('id', propertyId);
  return res.reasons.join('\n');
}
